// ** React Imports **
import React, { Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

// ** APP Page CSS **
import "./App.css";

// ** Common Component **
import FallbackSpinner from "./@core/components/spinner";

// ** Auth/Role Handler **
import AclGuard from "./@core/components/auth/AclGuard";
import { defaultACLObj } from "./configs/acl";

// ** Layouts **
// import BlankLayout from "./@core/layouts/BlankLayout";
import AuthLayout from "./layouts/AuthLayout";
import UserLayout from "./layouts/UserLayout";
import AuthGuard from "./@core/components/auth/AuthGuard";
import GuestGuard from "./@core/components/auth/GuestGuard";
import PublicLayout from "./layouts/PublicLayout";

// ** Context Providers **
import { SocketProvider } from "./context/SocketContext";
import { LocationProvider } from "./context/LocationContext";
import { ChatPopupProvider } from "./context/ChatPopupContext";
import { UploadProvider } from "./contexts/UploadContext";
import { AuthModalProvider } from "./context/AuthModalContext";

// ** Chat Popup Manager **
import ChatPopupManager from "./components/chat/ChatPopupManager";

// ** App Detection (runs once globally to avoid duplicate deep link probes) **
import { useAppDetection } from "./hooks/useAppDetection";

// ** Global Upload Indicator **
import GlobalUploadIndicator from "./components/GlobalUploadIndicator";

// ** Auth Modals **
import LoginModal from "./components/auth/LoginModal";
import SignupModal from "./components/auth/SignupModal";
import ForgotPasswordModal from "./components/auth/ForgotPasswordModal";
import OtpVerificationModal from "./components/auth/OtpVerificationModal";
import ResetPasswordModal from "./components/auth/ResetPasswordModal";

// ** Auth Pages Imports **
const LoginPage = React.lazy(() => import("src/pages/auth/login"));
const RegisterPage = React.lazy(() => import("src/pages/auth/register"));
const RegisterFlow = React.lazy(() => import("src/pages/auth/RegisterFlow"));
const ResetPasswordPage = React.lazy(() =>
  import("src/pages/auth/resetPassword")
);
const ForgotPasswordPage = React.lazy(() =>
  import("src/pages/auth/forgotPassword")
);
const OtpVerificationPage = React.lazy(() => import("src/pages/auth/otp"));

// ** User Pages **
const HomePage = React.lazy(() => import("src/pages/home/HomePage"));
const DashboardPage = React.lazy(() => import("src/pages/dashboard"));
const PlacesListing = React.lazy(() => import("src/pages/places/PlacesListing"));
const PeopleListing = React.lazy(() => import("src/pages/people/PeopleListing"));
const GroupsListing = React.lazy(() => import("src/pages/groups/GroupsListing"));
const RegisterBusiness = React.lazy(() =>
  import("src/pages/register-business")
);
const ProfilePage = React.lazy(() => import("src/pages/profile"));
const ProfileSettings = React.lazy(() => import("src/pages/profile-settings"));
const AccountPage = React.lazy(() => import("src/pages/account"));
const MyContacts = React.lazy(() => import("src/pages/my-contacts"));
const MyOrders = React.lazy(() => import("src/pages/my-orders"));
const PostPage = React.lazy(() => import("src/pages/post"));
const PostDetails = React.lazy(() => import("src/pages/post/PostDetails"));
const EditPost = React.lazy(() => import("src/pages/post/EditPost"));
const ChatPage = React.lazy(() => import("src/pages/chat"));
const TrustSettings = React.lazy(() => import("src/pages/trust-settings"));

// ** Event Pages **
const EventListing = React.lazy(() => import("src/pages/event/EventListing"));
const EventCreate = React.lazy(() => import("src/pages/event/EventCreate"));
const EventDetails = React.lazy(() => import("src/pages/event/EventDetails"));
const HomeAddressVerification = React.lazy(() =>
  import("src/pages/trust-settings/home-address-verification")
);
const IDVerification = React.lazy(() =>
  import("src/pages/trust-settings/id-verification")
);
const ReferencesVerification = React.lazy(() =>
  import("src/pages/trust-settings/references-verification")
);
const PermissionsPage = React.lazy(() => import("src/pages/permissions"));
const BusinessContacts = React.lazy(() =>
  import("src/pages/dashboard/business-contact/index")
);
const ContactsTabs = React.lazy(() =>
  import("src/pages/dashboard/team-member-linking")
);
const BusinessProfile = React.lazy(() => import("src/pages/business/profile"));
const BusinessDashboard = React.lazy(() =>
  import("src/pages/business/dashboard")
);
const OrdersPage = React.lazy(() =>
  import("src/pages/business/orders")
);
const FollowersList = React.lazy(() =>
  import("src/pages/dashboard/business-followers")
);
const BroadcastFollowersSelect = React.lazy(() =>
  import("src/pages/dashboard/business-followers/BroadcastFollowers")
);
const Request = React.lazy(() => import("src/pages/dashboard/request/index"));
const EmployeeRequest = React.lazy(() =>
  import("src/pages/dashboard/employee-request/index")
);
const EmployeeLeaveRequest = React.lazy(() =>
  import("src/pages/dashboard/employee-leave-request/index")
);
const TeamLeaveRequest = React.lazy(() =>
  import("src/pages/dashboard/team-leave-request/index")
);
const SetMultiplePermission = React.lazy(() =>
  import("src/pages/permissions/SetMultiplePermission")
);
const SubscriptionSuccess = React.lazy(() =>
  import("src/pages/subscription/Success")
);
const SubscriptionCancel = React.lazy(() =>
  import("src/pages/subscription/Cancel")
);
const ReportIssue = React.lazy(() =>
  import("src/pages/report-issue/index")
);
const NotificationsPage = React.lazy(() =>
  import("src/pages/notifications/index")
);
const TutorialVideosPage = React.lazy(() =>
  import("src/pages/tutorial-videos/index")
);

// ** Legal Pages **
const PrivacyPolicy = React.lazy(() =>
  import("src/pages/legal/PrivacyPolicy")
);
const TermsAndConditions = React.lazy(() =>
  import("src/pages/legal/TermsAndConditions")
);
const ChildPolicy = React.lazy(() =>
  import("src/pages/legal/ChildPolicy")
);
const SubscriptionTerms = React.lazy(() =>
  import("src/pages/legal/SubscriptionTerms")
);

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return (
      <GuestGuard fallback={<FallbackSpinner height="80vh" />}>
        {children}
      </GuestGuard>
    );
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>;
  } else {
    return (
      <AuthGuard fallback={<FallbackSpinner height="80vh" />}>
        {children}
      </AuthGuard>
    );
  }
};

function App() {
  const aclAbilities = defaultACLObj;

  useAppDetection();

  return (
    <Suspense fallback={<FallbackSpinner />}>
      <AuthModalProvider>
        <SocketProvider>
          <LocationProvider>
            <ChatPopupProvider>
              <UploadProvider>
                <AclGuard aclAbilities={aclAbilities}>
              <Routes>
            <Route
              element={
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              }
            >
              <Route
                element={
                  <Guard guestGuard>
                    <Outlet />
                  </Guard>
                }
              >
                <Route path="/" element={<Navigate to={"/home"} />} />
                <Route path="/login" element={<LoginPage />} />
                {/* Marketing site aliases - same pages, different URLs */}
                <Route path="/business/login" element={<LoginPage />} />
                <Route
                  path="/account/password-reset-request"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/otp" element={<OtpVerificationPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Route>
              {/* Business Registration Flow - No GuestGuard to allow multi-step completion */}
              <Route path="/register" element={<RegisterFlow />} />
              <Route path="/business/get-started" element={<RegisterFlow />} />
            </Route>

            <Route
              element={
                <PublicLayout>
                  <Outlet />
                </PublicLayout>
              }
            >
              <Route path="/home" element={<HomePage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/places" element={<PlacesListing />} />
              <Route path="/people" element={<PeopleListing />} />
              <Route path="/groups" element={<GroupsListing />} />
              <Route path="/register-business" element={<RegisterBusiness />} />
              <Route path="/profile/:id" element={<ProfilePage />} />
              <Route path="/profile-settings" element={<ProfileSettings />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/my-contacts" element={<MyContacts />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route
                path="/business-profile/:id"
                element={<BusinessProfile />}
              />
              <Route
                path="/business-dashboard/:id/:tab?"
                element={<BusinessDashboard />}
              />
              <Route path="/business-orders/:businessId?" element={<OrdersPage />}></Route>
              <Route path="/post" element={<PostPage />}></Route>
              <Route path="/post/:postId" element={<PostDetails />}></Route>
              <Route path="/post/edit/:postId" element={<EditPost />}></Route>
              <Route
                path="/dashboard/business-contact"
                element={<BusinessContacts />}
              ></Route>
              <Route
                path="/dashboard/business-followers"
                element={<FollowersList />}
              ></Route>
              <Route
                path="/dashboard/business-followers/broadcast"
                element={<BroadcastFollowersSelect />}
              ></Route>
              <Route
                path="/dashboard/team-member-linking"
                element={<ContactsTabs />}
              ></Route>
              <Route path="/dashboard/request" element={<Request />}></Route>
              <Route
                path="/dashboard/employee-request"
                element={<EmployeeRequest />}
              ></Route>
              <Route
                path="/dashboard/employee-leave-request"
                element={<EmployeeLeaveRequest />}
              ></Route>
              <Route
                path="/dashboard/team-leave-request"
                element={<TeamLeaveRequest />}
              ></Route>
              <Route
                path="/dashboard/:businessId/team-leave-request"
                element={<TeamLeaveRequest />}
              ></Route>
              <Route path="/trust-settings" element={<TrustSettings />}></Route>
              <Route
                path="/trust-settings/home-address-verification"
                element={<HomeAddressVerification />}
              ></Route>
              <Route
                path="/trust-settings/id-verification"
                element={<IDVerification />}
              ></Route>
              <Route
                path="/trust-settings/references-verification"
                element={<ReferencesVerification />}
              ></Route>
              <Route path="/permissions" element={<PermissionsPage />}></Route>
              <Route
                path="/set-permissions"
                element={<SetMultiplePermission />}
              ></Route>
              <Route path="/chats" element={<ChatPage />}></Route>
              <Route
                path="/chats/:conversationId"
                element={<ChatPage />}
              ></Route>
              <Route path="/events" element={<EventListing />}></Route>
              <Route path="/events/create" element={<EventCreate />}></Route>
              <Route path="/events/:eventId" element={<EventDetails />}></Route>
              <Route
                path="/subscription/success"
                element={<SubscriptionSuccess />}
              ></Route>
              <Route
                path="/subscription/cancel"
                element={<SubscriptionCancel />}
              ></Route>
              <Route path="/report" element={<ReportIssue />}></Route>
              <Route path="/notifications" element={<NotificationsPage />}></Route>
              <Route path="/tutorial-videos/:type?" element={<TutorialVideosPage />}></Route>
              <Route path="/privacy-policy" element={<PrivacyPolicy />}></Route>
              <Route path="/terms-and-conditions" element={<TermsAndConditions />}></Route>
              <Route path="/child-policy" element={<ChildPolicy />}></Route>
              <Route path="/subscription-terms" element={<SubscriptionTerms />}></Route>
              {/* <Route path="*" element={<>Coming Soon Page</>} /> */}
            </Route>
            </Routes>
            <ChatPopupManager />
            <GlobalUploadIndicator />
            {/* Auth Modals - Global */}
            <LoginModal />
            <SignupModal />
            <ForgotPasswordModal />
            <OtpVerificationModal />
            <ResetPasswordModal />
          </AclGuard>
              </UploadProvider>
        </ChatPopupProvider>
      </LocationProvider>
    </SocketProvider>
  </AuthModalProvider>
</Suspense>
  );
}

export default App;
